<template lang="pug">
  div(class="billing-invoices")
    div(
      v-if="invoices.length"
      class="billing-invoices__header"
    )
      div
        h4(class="text-16px fw-500 dark mb-1") {{ $t('Control your expenses') }}
        div(class="text-12px fw-300") {{ $t('Transparency of each transaction — no hidden fees, only the result') }}
      m-block-table-pagination(
        :meta="meta"
        @prev="prevPage"
        @next="nextPage"
      )
    div(class="billing-invoices__table")
      m-table(
        store-name="invoices"
        :headers="headers"
        :hide-default-header="!invoices.length"
        hide-default-footer
        :empty-block="emptyBlock"
        :item-actions="actions"
        :mobile-item-config="mobileItemConfig"
        ref="table"
      )
        template(v-slot:emptyTableActions)
          v-btn(
            v-if="!$hasProVersion"
            class="main-button"
            :loading="updateToPremiumLoading"
            @click="updateToPremium(false)"
          ) {{ $t('Update to Premium') }}
          v-btn(
            class="secondary-button"
            @click="goBuyDocuments"
          ) {{ $t('Buy documents') }}
</template>

<script>
import { mapGetters } from 'vuex'
import MTable from '~/components/modules/tables/m-table'
import actions from '~/modules/billing/mixins/actions'
import MBlockTablePagination from '~/components/modules/blocks/concrete/m-block-table-pagination'

export default {
  name: 'BillingInvoices',
  components: {
    MBlockTablePagination,
    MTable
  },
  mixins: [actions],
  data() {
    return {
      loading: false
    }
  },
  computed: {
    ...mapGetters('invoices', {
      invoices: 'items',
      meta: 'meta'
    }),
    invoiceStatuses() {
      return [
        {
          value: 'paid',
          text: this.$t('Payed'),
          background: 'rgba(33, 150, 83, 0.08)',
          color: '#219653'
        },
        {
          value: 'created',
          text: this.$t('Awaits'),
          background: 'rgba(255, 170, 1, 0.08)',
          color: '#FFAA01'
        },
        {
          value: 'cancelled',
          text: this.$t('Cancelled'),
          background: 'rgba(255, 23, 68, 0.08)',
          color: '#FF1744'
        }
      ]
    },
    headers() {
      return [
        {
          text: 'Organization',
          value: this.$Organization?.name,
          sortable: false,
          filterable: false
        },
        {
          text: 'Payment date',
          value: 'payedAt',
          valueFormatter: invoice =>
            invoice.payedAt ? this.$moment(invoice.payedAt).format('DD.MM.YYYY, HH:mm') : '-',
          sortable: false,
          filterable: false
        },
        {
          text: 'Sum, uah',
          value: 'totalSum',
          valueFormatter: invoice => (invoice.totalSum / 100).toFixed(2),
          sortable: false,
          filterable: false,
          align: 'center'
        },
        {
          text: 'Payment code',
          value: 'number',
          sortable: false,
          filterable: false
        },
        {
          text: 'Status',
          value: 'status',
          component: 'e-status-chip',
          componentProps: {
            statuses: this.invoiceStatuses
          },
          sortable: false,
          filterable: false
        },
        {
          text: 'Actions',
          align: 'center',
          value: 'actions',
          width: '120',
          sortable: false,
          filterable: false
        }
      ]
    },
    emptyBlock() {
      return {
        title: this.$t('Start your financial story'),
        subtitle: this.$t(
          'Your invoices will appear here after your first purchase. Start with Premium or additional documents'
        ),
        image: 'money-with-wings',
        imageExtension: 'gif'
      }
    },
    mobileItemConfig() {
      return {
        title: this.$Organization?.name,
        subtitle: {
          value: 'status',
          component: 'e-status-chip',
          componentProps: {
            statuses: this.invoiceStatuses,
            onlyText: true
          }
        },
        additionalData: [
          {
            valueFormatter: invoice =>
              invoice.payedAt ? this.$moment(invoice.payedAt).format('DD.MM.YYYY, HH:mm') : '-'
          },
          {
            valueFormatter: invoice => this.$t('Sum') + ': ' + (invoice.totalSum / 100).toFixed(2)
          }
        ]
      }
    }
  },
  methods: {
    goBuyDocuments() {
      this.$emit('goToTab', 0)
    },
    async prevPage() {
      await this.tableRequest({ offset: this.meta.currentPage - 1 })
    },
    async nextPage() {
      await this.tableRequest({ offset: this.meta.currentPage + 1 })
    },
    async tableRequest(params) {
      if (this.$refs.table && this._.isFunction(this.$refs.table.fetchData)) {
        await this.$refs.table.fetchData(params)
      }
    }
  }
}
</script>

<style scoped lang="scss">
.billing-invoices {
  border-radius: 16px;
  border: 1px solid $border-disabled;
  padding-top: 16px;
  height: 100%;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding: 0 16px;
  }

  &__table {
    height: 100%;
  }
}
</style>
